import urlJoin from 'url-join';
import { AUTHORIZATION_URL, ProductAPIMap, STATIC_PRODUCT_MENUS } from '../constants';
import { APIMenuDefinition, FeatureFlagsConfig, ProductSlug } from '../types';
import { APIService } from './api/api-service';

/**
 * Module-scoped data store shared by every platform-menu element on the page
 * (`platform-header`, `platform-menu-sidebar`, ...). Fetches are promise-deduped:
 * whichever element asks first triggers the network call and every other caller
 * awaits the same promise, so siblings never duplicate requests.
 *
 * Lifetime / data-leakage invariant: this cache lives exactly as long as the
 * document. Logging out always crosses a full navigation (`/session/logout`), so
 * a different user logging in within the same browser session starts from an
 * empty store — nothing here can leak between users. The one persisted entry,
 * the feature-flag config in sessionStorage, is environment-scoped (LaunchDarkly
 * client ID + URLs), not user- or tenant-scoped.
 */

const api = new APIService();

const resets = new Set<() => void>();

function cached<T>(fetcher: () => Promise<T>): () => Promise<T> {
  let promise: Promise<T> | undefined;
  resets.add(() => (promise = undefined));
  return () =>
    (promise ??= fetcher().catch((err) => {
      // Failed fetches are not cached; the next caller retries.
      promise = undefined;
      throw err;
    }));
}

export const getSiteData = cached(() => api.getSiteData());

export const getUserSiteConfigs = cached(async () => {
  const sites = await api.getUserSiteConfigs();
  if (sites.length === 0) {
    // The sites API answers [] instead of erroring (both server-side and in
    // APIService), so an empty result may be an outage in disguise. Surface it
    // to this caller but don't cache it, so the next caller retries. Real
    // accounts always have at least one site.
    throw new Error('Error loading site configs: empty result');
  }
  return sites;
});

export const getUserInfo = cached(() => api.getUserInfo());

export const getCurrentSiteFeatures = cached(() => api.getCurrentSiteFeatures());

const FLAG_CONFIG_KEY = 'flagConfig';

/**
 * LaunchDarkly initialization needs every field, and the sessionStorage cache
 * can hold a stale shape from an older deploy in the same tab session.
 */
export function isCompleteFlagConfig(value: FeatureFlagsConfig | null | undefined): value is FeatureFlagsConfig {
  return (
    typeof value?.platformClientID === 'string' &&
    typeof value.baseURI === 'string' &&
    typeof value.streamURI === 'string' &&
    typeof value.eventsURI === 'string'
  );
}

export const getFeatureFlagsConfig = cached(async (): Promise<FeatureFlagsConfig> => {
  try {
    const stored = sessionStorage.getItem(FLAG_CONFIG_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (isCompleteFlagConfig(parsed)) {
        return parsed;
      }
      // Stale or partial entry: ignore it and refetch below (which overwrites it).
    }
  } catch {
    // Storage unavailable or a corrupted entry: fall through to fetch (a success
    // overwrites the bad entry below), so one bad write can't poison the session.
  }
  const config = await api.getFeatureFlagsConfig();
  try {
    sessionStorage.setItem(FLAG_CONFIG_KEY, JSON.stringify(config));
  } catch {
    // Storage unavailable: the config still works for this page, just not cached.
  }
  return config;
});

const productMenus = new Map<ProductSlug, Promise<APIMenuDefinition>>();
resets.add(() => productMenus.clear());

export function getProductMenu(product: ProductSlug): Promise<APIMenuDefinition> {
  const staticMenu = STATIC_PRODUCT_MENUS[product];
  if (staticMenu) {
    return Promise.resolve(staticMenu);
  }
  const existing = productMenus.get(product);
  if (existing) {
    return existing;
  }
  const promise = fetchProductMenu(product).catch((err) => {
    productMenus.delete(product);
    throw err;
  });
  productMenus.set(product, promise);
  return promise;
}

async function fetchProductMenu(product: ProductSlug): Promise<APIMenuDefinition> {
  const apiInfo = ProductAPIMap[product];
  if (apiInfo == undefined) {
    throw new Error(`Error loading menu data: no API mapping for product "${product}"`);
  }
  const resp = await fetch(apiInfo.apiURL, { headers: { Accept: 'application/json' } });
  if (!resp.ok) {
    throw new Error(`Error loading menu data for product "${product}" (HTTP ${resp.status})`);
  }
  const parsed = JSON.parse((await resp.text()).replace(/^\)]}',/, ''));
  return apiInfo.transformer ? apiInfo.transformer(parsed) : parsed;
}

/**
 * Switch the user's preferred tenant and navigate to it (GLOBAL sites land on
 * /admin). Shared by the header's site selector and the sidebar's
 * Administration link.
 */
export async function selectSite(siteId: string): Promise<void> {
  try {
    const sites = await getUserSiteConfigs();
    const redirectPath = sites.find((s) => s.site_id === siteId)?.residency === 'GLOBAL' ? '/admin' : '/';
    await api.setPreferredTenant(siteId);
    window.location.assign(urlJoin(AUTHORIZATION_URL, redirectPath));
  } catch (err) {
    // Callers are fire-and-forget event handlers with no failure UI; log rather
    // than surface an unhandled rejection. The user stays on the current site.
    console.error(`[platform-menu] Failed to switch to site "${siteId}"`, err);
  }
}

/** Clears every cached fetch. Test hook — production code never needs it. */
export function resetMenuDataStore() {
  for (const reset of resets) {
    reset();
  }
}
